/* globals VXConfig */

import Translations from '../utils/Translations';
import Constants from '../flux/Constants';
import cookieLite from '../components/CookieBanner/CookieLite';
import classNames from 'classnames';
import Routes from './Routes';

// Generate unique key for React child in a list
export const generateKey = (pre, index = '') => {
	pre = pre
		? pre
				.toString()
				.toLowerCase()
				.normalize('NFD')
				.replace(/[\u0300-\u036f]/g, '')
				.replace(/\s/g, '-')
		: 'u-key';
	pre = index ? `${pre}-${index}` : pre;
	return pre;
};

const parseGeoLocation = (geoLocation) => [geoLocation.lat, geoLocation.long];
export const fetchGeoLocationByZip = async (zipCode) => {
	const headers = new Headers({
		'Content-Type': 'application/json',
	});

	const init = {
		headers: headers,
		method: 'GET',
		credentials: 'include',
	};

	const url = Routes.getRoute(Routes.Names.UTILITY_GET_GEO_LOCATION_BY_ZIP_CODE).replace(':zipCode', zipCode);
	return doFetch(url, null, Constants.HttpMethods.GET, true, init).then((result) => {
		if (result.success && result.data) {
			const geoLocation = JSON.parse(result.data);
			if (geoLocation) {
				const parsedLocation = parseGeoLocation(geoLocation);
				return parsedLocation;
			}
		}
		throw new Error('No geoLocation found');
	});
};

/* Helper to work with CSS module classes more easily

Use-Case Example:
Declare: const cn = (...classes) => classNamesForModules(styles, ...classes)
Use: className={ cn("class-name__a", "class-name__b", isMobile && "-is-mobile") } */

export const classNamesForModules = (styles, ...classes) => classNames(classes.map((c) => c && styles[c]));

export const isObjectEmpty = (obj) => {
	if (obj === null || obj === undefined) return true;
	return Object.keys(obj).length === 0;
};

const addClass = function (element, className) {
	if (element && typeof element === 'object' && className) {
		element.classList.add(className);
	}
};

const removeClass = function (element, className) {
	if (element && typeof element === 'object' && className) {
		element.classList.remove(className);
	}
};

const getElementWidth = function (element) {
	if (!element) {
		return 0;
	}

	let width = getComputedStyle(element).width;
	width = width.substring(0, width.length - 2);

	return parseInt(width);
};

const getElementHeight = function (element) {
	if (!element) {
		return 0;
	}

	let height = getComputedStyle(element).height;
	height = height.substring(0, height.length - 2);

	return parseInt(height);
};

const getOffsetTop = function (element) {
	if (!element) {
		return 0;
	}

	const rect = element.getBoundingClientRect();

	return rect.top + window.scrollY;
};

const getOffsetLeft = function (element) {
	if (!element) {
		return 0;
	}

	const rect = element.getBoundingClientRect();

	return rect.left + window.scrollX;
};

/**
 * returns the position of an given element
 * @param eID string
 * @returns integer
 */
const elmYPosition = function (eID) {
	const elm = document.getElementById(eID);
	let y = elm.offsetTop;
	let node = elm;
	while (node.offsetParent && node.offsetParent !== document.body) {
		node = node.offsetParent;
		y += node.offsetTop;
	}
	return y;
};
/**
 * returns the current position
 * @returns currentpos|0 int
 */
const currentYPosition = function () {
	// Firefox, Chrome, Opera, Safari
	if (self.pageYOffset) {
		return self.pageYOffset;
	}
	// Internet Explorer 6 - standards mode
	if (document.documentElement && document.documentElement.scrollTop) {
		return document.documentElement.scrollTop;
	}
	// Internet Explorer 6, 7 and 8
	if (document.body.scrollTop) {
		return document.body.scrollTop;
	}
	return 0;
};

/**
 * function to scroll up/down from one element to another
 * @param eID     string
 * @param animate bool
 * @param {Number} offset
 */
const scrollToElem = function (eID, animate, offset = 0) {
	const el = document.getElementById(eID);
	if (!el) {
		return;
	}

	animate = typeof animate === 'undefined' || animate === true;
	const startPosY = currentYPosition();
	const stopPosY = elmYPosition(eID) - offset;
	const distance = stopPosY > startPosY ? stopPosY - startPosY : startPosY - stopPosY;
	if (distance < 100 || !animate) {
		scrollTo(0, stopPosY);
		return;
	}
	let speed = Math.round(distance / 100);
	if (speed >= 20) {
		speed = 20;
	}
	const step = Math.round(distance / 25);
	let leapY = stopPosY > startPosY ? startPosY + step : startPosY - step;
	let timer = 0;

	if (stopPosY > startPosY) {
		for (let i = startPosY; i < stopPosY; i += step) {
			setTimeout(window.scrollTo, timer * speed, 0, leapY);
			leapY += step;
			if (leapY > stopPosY) {
				leapY = stopPosY;
			}
			timer++;
		}
		return;
	}
	for (let i = startPosY; i > stopPosY; i -= step) {
		setTimeout(window.scrollTo, timer * speed, 0, leapY);
		leapY -= step;
		if (leapY < stopPosY) {
			leapY = stopPosY;
		}
		timer++;
	}
};

/**
 * function to scroll up/down from one element to another within a container
 * @param eID     string
 * @param cID     string
 * @param animate bool
 * @param forcedSpeed number
 */
const scrollContainerToElem = function (eID, cID, animate, forcedSpeed) {
	animate = typeof animate === 'undefined' || animate === true;

	const target = document.getElementById(eID);
	const container = document.getElementById(cID);

	if (!target || !container) {
		return;
	}

	const startPosY = container.scrollTop;
	const stopPosY = target.offsetTop;
	const distance = stopPosY > startPosY ? stopPosY - startPosY : startPosY - stopPosY;
	if (distance < 100 || !animate) {
		container.scrollTop = stopPosY;
		return;
	}

	let speed;
	if (typeof forcedSpeed === 'number') {
		speed = forcedSpeed;
	} else {
		speed = Math.round(distance / 100);
		if (speed >= 20) {
			speed = 20;
		}
	}

	const step = Math.round(distance / 25);
	let leapY = stopPosY > startPosY ? startPosY + step : startPosY - step;
	let timer = 0;
	if (stopPosY > startPosY) {
		for (let i = startPosY; i < stopPosY; i += step) {
			setTimeout(
				function (topY) {
					container.scrollTop = topY;
				}.bind(null, leapY),
				timer * speed,
			);
			leapY += step;
			if (leapY > stopPosY) {
				leapY = stopPosY;
			}
			timer++;
		}
		return;
	}
	for (let i = startPosY; i > stopPosY; i -= step) {
		setTimeout(
			function (topY) {
				container.scrollTop = topY;
			}.bind(null, leapY),
			timer * speed,
		);
		leapY -= step;
		if (leapY < stopPosY) {
			leapY = stopPosY;
		}
		timer++;
	}
};

/**
 * @param element
 * @param fn       The function that performs CSS manipulations that should not be transitioned
 *
 * @return string
 */
const disableTransition = function (element, fn) {
	const transitionDisabledClass = 'h-transition-disabled';

	// disable transitions
	element.classList.remove(transitionDisabledClass);

	// perform css changes
	fn();

	// noinspection BadExpressionStatementJS
	element.offsetHeight;

	// enable transitions
	element.classList.remove(transitionDisabledClass);
};

// proudly copy & pasted (and slightly adapted) from http://stackoverflow.com/questions/486896/adding-a-parameter-to-the-url-with-javascript
const getUrlWithinsertedGetParam = function (key, value) {
	key = encodeURI(key);
	value = encodeURI(value);
	const search = document.location.search.substr(1);

	let kvp;
	if (search) {
		kvp = search.split('&');
	} else {
		kvp = [];
	}

	let i = kvp.length;
	let x;
	while (i--) {
		x = kvp[i].split('=');

		if (x[0] === key) {
			x[1] = value;
			kvp[i] = x.join('=');
			break;
		}
	}

	if (i < 0) {
		kvp[kvp.length] = [key, value].join('=');
	}

	return '?' + kvp.join('&');
};

const getUrlParams = function (url) {
	url = url.split('?')[1];
	url = url ? url.split('#')[0] : url;

	const params = {};
	const re = /[?&]?([^=]+)=([^&]*)/g;
	let tokens;

	while ((tokens = re.exec(url))) {
		params[decodeURIComponent(tokens[1])] = decodeURIComponent(tokens[2]);
	}

	return params;
};

const removeUrlParam = function (key, sourceURL) {
	let rtn = sourceURL.split('?')[0],
		param,
		params_arr = [];
	const queryString = sourceURL.indexOf('?') !== -1 ? sourceURL.split('?')[1] : '';
	if (queryString !== '') {
		params_arr = queryString.split('&');
		for (let i = params_arr.length - 1; i >= 0; i -= 1) {
			param = params_arr[i].split('=')[0];
			if (param === key) {
				params_arr.splice(i, 1);
			}
		}
		if (params_arr.length !== 0) {
			rtn = rtn + '?' + params_arr.join('&');
		}
	}
	return rtn;
};

const updateURLParameter = function (url, param, paramVal) {
	let newAdditionalURL = '';
	let tempArray = url.toString().split('?');
	const baseURL = tempArray[0];
	const additionalURL = tempArray[1];
	let temp = '';
	if (additionalURL) {
		tempArray = additionalURL.split('&');
		for (let i = 0; i < tempArray.length; i++) {
			if (tempArray[i].split('=')[0] !== param) {
				newAdditionalURL += temp + tempArray[i];
				temp = '&';
			}
		}
	}

	const rows_txt = temp + '' + param + '=' + paramVal;
	return baseURL + '?' + newAdditionalURL + rows_txt;
};

const setUrlParameters = function (url, parameters) {
	const baseUrl = url.toString().split('?')[0];
	const keys = Object.keys(parameters);

	if (keys.length === 0) {
		return baseUrl;
	}

	return baseUrl + '?' + keys.map((key) => `${key}=${parameters[key]}`).join('&');
};

//const parseQuery = (search = '') => qs.parse(search);
const parseQuery = (search = '') => {
	const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
	const result = {};
	for (const [key, value] of params) {
		result[key] = params.getAll(key).length > 1 ? params.getAll(key) : value;
	}
	return result;
};

const formatChatPrice = function (number) {
	return formatCurrency(number) + ' / min';
};

/**
 * @param {number} number
 * @param {null|boolean} stripZeros if null, zeros will be stripped or not depending on currency
 * @param {string} currency
 * @param {boolean} whitespace
 * @returns {string}
 */
const formatCurrency = function (number, stripZeros = true, currency = Constants.Currency.VXC, whitespace = true) {
	let formatted = '';

	let fixed = number.toFixed(getCurrencyDecimals(currency)).replace('.', ',');

	if (stripZeros || (shouldStripZeros(currency) && stripZeros !== false)) {
		fixed = fixed.replace(',00', '');
	}

	formatted = fixed;

	if (whitespace) {
		formatted += '\u00a0';
	}

	return formatted + getSymbolForCurrency(currency);
};

const DEFAULT_COIN_CHARGE_FACTOR = 4;
const eurToCoin = (eur) => Math.ceil(eur * DEFAULT_COIN_CHARGE_FACTOR);
const coinToEur = (coins) => {
	const parsedCoins = Number(coins);

	if (!Number.isFinite(parsedCoins) || parsedCoins <= 0) {
		return 0;
	}

	return Math.ceil(parsedCoins / DEFAULT_COIN_CHARGE_FACTOR);
};

const getSymbolForCurrency = function (currency) {
	let symbol;

	switch (currency) {
		case Constants.Currency.EUR:
			symbol = Constants.CurrencySymbol.EUR;
			break;
		case Constants.Currency.CHF:
			symbol = Constants.CurrencySymbol.CHF;
			break;
		case Constants.Currency.VOC:
			symbol = Constants.CurrencySymbol.VOC;
			break;
		case Constants.Currency.VOU:
			symbol = Constants.CurrencySymbol.VOU;
			break;
		case Constants.Currency.VXC:
			symbol = Constants.CurrencySymbol.VXC;
			break;
		default:
			throw new Error('Unknown currency');
	}

	return symbol;
};

const shouldStripZeros = function (currency) {
	return [Constants.Currency.VOC, Constants.Currency.VOU, Constants.Currency.VXC].includes(currency);
};

const getStaticAmount = (key) => VXConfig.staticAmounts[key];

const formatDateHM = function (date) {
	if (date instanceof Date) {
		return date.getHours() + ':' + (date.getMinutes() < 10 ? '0' + date.getMinutes() : date.getMinutes());
	} else {
		return '';
	}
};

const formatDuration = function (duration, withMin = true) {
	let hours = Math.floor(duration / 3600).toString();
	let minutes = Math.floor((duration % 3600) / 60).toString();
	let seconds = (duration % 60).toString();

	if (hours.length < 2) {
		hours = '0' + hours;
	}
	if (minutes.length < 2) {
		minutes = '0' + minutes;
	}
	if (seconds.length < 2) {
		seconds = '0' + seconds;
	}
	let durationFormated = minutes + ':' + seconds;
	if (hours !== '00') {
		durationFormated = hours + ':' + durationFormated;
	}

	return durationFormated + (withMin ? ' min' : '');
};

const formatLargeNumber = function (number, max = null) {
	const isNegative = number < 0;
	const aboveMax = !!(max !== null && number > max);
	number = (aboveMax ? max : Math.abs(number)) + '';

	let numberFormatted = '';
	for (let i = number.length; i > 0; i -= 3) {
		numberFormatted = number.substring(Math.max(0, i - 3), i) + (numberFormatted ? '.' + numberFormatted : numberFormatted);
	}

	if (isNegative) {
		numberFormatted = '-' + numberFormatted;
	} else if (aboveMax) {
		numberFormatted = numberFormatted + '+';
	}

	return numberFormatted;
};

/**
 * Adds leading zeros to a number to get a specific length
 * @param number
 * @param length
 * @returns {string}
 */
const formatNumberToLength = function (number, length) {
	return '0'.repeat(Math.max(length - number.toString().length, 0)) + number;
};

const formatMessagePriceWithFreeMessages = function (messagePrice, freeMessages = 0) {
	// fallback
	if (typeof messagePrice === 'string') {
		return messagePrice;
	}

	if (messagePrice === 0.0) {
		return Translations.get('ChatButtonFree');
	}

	return messagePrice > 0.0 && freeMessages === 0 ? formatCurrency(messagePrice) : freeMessages + ' ' + Translations.get('common.freeMessages');
};

/**
 * @param text
 * @param replaceArgs Object containing all arguments to replace
 *
 * @return string
 */
const replaceTranslationMarkers = function (text, replaceArgs) {
	return replaceArgs
		? text.replace(/(:\w+)/g, function (m, key) {
				return Object.hasOwn(replaceArgs, key) ? replaceArgs[key] : key;
			})
		: text;
};

const isMobileRequest = function () {
	return document.documentElement.classList.contains('mobile');
};

const isTouchDevice = function () {
	const selector = document.documentElement;
	return selector.classList.contains('mobile') || selector.classList.contains('tablet');
};

/**
 * Gets a cookie
 * @param cname
 * @returns {*}
 */
const getCookie = (cname) => cookieLite(cname);

/**
 * @param args
 * @param {String} action
 * @param {String} event
 * @param {String} [eventCategory]
 * @param {String} [eventAction]
 * @param {String} [eventLabel]
 * @param {String} [eventValue]
 * @param {String} [fieldsObject]
 *
 * @example gaHelper('send', 'event', [eventCategory], [eventAction], [eventLabel], [eventValue], [fieldsObject]);
 * @link https://developers.google.com/analytics/devguides/collection/analyticsjs/events
 */
const gaHelper = (...args) => {
	if (typeof window.ga === 'function') {
		window.ga.apply(null, args);
	}
};

const gtagHelper = (...args) => {
	if (typeof window.gtag === 'function') {
		window.gtag.apply(null, args);
	}
};

function getLocalItemCategory(key) {
	if ([Constants.LocalItemNames.GLOBAL_SEARCH_REQUESTS].includes(key)) {
		return Constants.CookieCategories.FUNCTIONAL;
	}

	return Constants.CookieCategories.NECESSARY;
}

/**
 *
 * @param cname Cookie name
 * @returns {string}
 */
function getCookieCategory(cname) {
	if ([].includes(cname)) {
		return Constants.CookieCategories.FUNCTIONAL;
	}

	return Constants.CookieCategories.NECESSARY;
}

/**
 *
 * @param {string} category Cookie category
 * @returns {boolean}
 */
function isCookieCategoryEnabled(category) {
	const api = window.__cookieConsentApi;
	if (!api) {
		return true;
	}
	if (typeof api.allowedCategory === 'function') {
		return api.allowedCategory(category) || category === Constants.CookieCategories.NECESSARY;
	}
	return category === Constants.CookieCategories.NECESSARY;
}

function openCookieConsent() {
	const api = window.__cookieConsentApi;
	if (api && typeof api.showSettings === 'function') {
		api.showSettings();
	}
}

/**
 * Set a cookie
 * @param cname
 * @param cvalue
 * @param exdays
 * @param path
 * @param domain
 * @param secure
 * @param sameSiteValue
 */
function setCookie(cname, cvalue, exdays, path = '/', domain = '', secure = false, sameSiteValue = '') {
	const api = window.__cookieConsentApi;
	if (!api || isCookieEnabled(cname)) {
		cookieLite(cname, cvalue, exdays * 24 * 60 * 60, path, domain, secure, sameSiteValue);
	}
}

function isCookieEnabled(cname) {
	return isCookieCategoryEnabled(getCookieCategory(cname));
}

function isLocalItemEnabled(cname) {
	return isCookieCategoryEnabled(getLocalItemCategory(cname));
}

/**
 *
 * @param {string} url
 * @param {object} params
 * @returns {string}
 */
function urlWithQuery(url, params) {
	const query = Object.keys(params)
		.filter((k) => params[k] !== undefined)
		.map((k) => encodeURIComponent(k) + '=' + encodeURIComponent(params[k]))
		.join('&');
	url += (url.indexOf('?') === -1 ? '?' : '&') + query;
	return url;
}

/**
 * @param {string} absoluteUrl
 * @returns {string}
 */
function absoluteToRelativeURL(absoluteUrl) {
	const url = new URL(absoluteUrl);
	return url.pathname + url.search + url.hash; // Includes path, query, and fragment
}

/**
 * Path-only label for fetch errors (no query string — may contain tokens).
 *
 * @param {string} route
 * @param {string} [responseUrl]
 * @returns {string}
 */
function requestPathForError(route, responseUrl) {
	const raw = responseUrl || route;
	if (typeof raw !== 'string') {
		return String(route).split('?')[0];
	}
	try {
		return new URL(raw, window.location.href).pathname;
	} catch {
		return raw.split('?')[0];
	}
}

// fetch() network failures: Chrome, Safari, Firefox
const NETWORK_ERROR_MESSAGES = new Set(['Failed to fetch', 'Load failed', 'NetworkError when attempting to fetch resource.']);

/**
 *
 * @param {string} route
 * @param {object} payload
 * @param {string} method
 * @param {boolean} setCredentials
 * @param {object} init
 * @param {boolean} asBlob
 */
function doFetch(route, payload, method, setCredentials = false, init = {}, asBlob = false, useAuthentication = true) {
	const defaultInit = {
		method: method,
		headers: {},
	};

	if (useAuthentication && VXConfig.accessToken && VXConfig.accessToken.length > 0) {
		Object.assign(defaultInit.headers, {
			Authorization: VXConfig.accessToken,
		});
	}

	if (!asBlob) {
		Object.assign(defaultInit.headers, {
			'Content-Type': 'application/json',
		});
	}

	const mergedInit = mergeDeep(defaultInit, init);

	if (setCredentials) {
		mergedInit.credentials = 'include';
	} else {
		mergedInit.credentials = 'omit';
	}

	if (method !== Constants.HttpMethods.GET && method !== Constants.HttpMethods.HEAD) {
		mergedInit.body = JSON.stringify(payload);
	} else if (method === Constants.HttpMethods.GET && payload) {
		route = urlWithQuery(route, payload);
	}
	const requestPath = requestPathForError(route);

	return fetch(route, mergedInit)
		.then(function (response) {
			if (response.ok) {
				return asBlob ? response.blob() : response.json();
			}
			const failedPath = requestPathForError(route, response.url);
			throw new Error(`HTTP ${response.status} ${method} ${failedPath}`);
		})
		.catch(function (error) {
			if (error instanceof TypeError && NETWORK_ERROR_MESSAGES.has(error.message)) {
				const specified = new TypeError(`Failed to fetch ${method} ${requestPath}`);
				specified.cause = error;
				throw specified;
			}
			throw error;
		});
}

function isObject(item) {
	return item && typeof item === 'object' && !Array.isArray(item);
}

/**
 *
 * @param   {object} target
 * @param   {...object} sources
 * @returns {object}
 */
function mergeDeep(target, ...sources) {
	if (!sources.length) {
		return target;
	}
	const source = sources.shift();

	if (isObject(target) && isObject(source)) {
		for (const key in source) {
			if (Object.hasOwn(source, key)) {
				if (isObject(source[key])) {
					if (!target[key]) {
						target[key] = {};
					}
					mergeDeep(target[key], source[key]);
				} else {
					Object.assign(target, { [key]: source[key] });
				}
			}
		}
	}
	return mergeDeep(target, ...sources);
}

/**
 *
 * @param {string} value
 * @returns {number}
 */
function generateHash(value) {
	let hash = 0,
		i,
		chr;
	if (value.length === 0) {
		return hash;
	}
	for (i = 0; i < value.length; i++) {
		chr = value.charCodeAt(i);
		hash = (hash << 5) - hash + chr;
		hash |= 0; // Convert to 32bit integer
	}
	return hash;
}

/**
 *
 * @param string
 * @returns {string}
 */
function capitalizeFirstLetter(string) {
	return string.charAt(0).toUpperCase() + string.slice(1);
}

/**
 * @param {Function} callback
 * @param {Number} limit
 * @param {Boolean} trailing
 * @return {function()}
 */
const throttle = function (callback, limit, trailing = false) {
	let wait = false;
	let after = false;

	return () => {
		if (!wait) {
			callback.call();
			wait = true;
			setTimeout(() => {
				wait = false;
				if (trailing && after) {
					callback.call();
					after = false;
				}
			}, limit);
		} else if (trailing) {
			after = true;
		}
	};
};

/**
 * @return {boolean}
 */
let _supportsPassive = null;
const supportsPassiveEvents = function () {
	// check cache first
	if (_supportsPassive !== null) {
		return _supportsPassive;
	}

	/* oxlint-disable getter-return */
	let passive = false;
	const options = {
		get passive() {
			passive = true;
		},
	};
	/* oxlint-enable getter-return */

	try {
		document.addEventListener('test', null, options);
		document.removeEventListener('test', null, options);
	} catch {
		// do nothing
	}

	_supportsPassive = passive; // set cache

	return passive;
};

/**
 * Checks if the callbacks provide the correct dataset
 * Returns the dataset or null if the dataset is incomplete/missing
 *
 * @param {Function} getItems
 * @param {Function} getTotalCount
 * @param {Number} count
 * @param {Number} offset
 * @returns {null|[]}
 */
const getCompleteDataset = function (getItems, getTotalCount, count, offset) {
	const allItems = getItems();
	const totalCount = getTotalCount();
	let items = [];

	if (allItems) {
		for (let i = offset; i < Math.min(count + offset, totalCount); i++) {
			const item = allItems[i];

			if (!item) {
				// continue if item is missing
				return null;
			}

			items[i - offset] = item;
		}
	} else {
		items = null;
	}

	return items;
};

const addPreventScrolling = () => {
	if (document.querySelector('.prevent-scrolling') === null) {
		document.getElementById('mobile-content-wrapper') &&
			document.getElementById('mobile-content-wrapper').classList.add('mobile-content-wrapper--fixed');
		document.querySelector('body').classList.add('prevent-scrolling');
		if (document.querySelector('html.mobile') !== null && window.Flux.Browser.isSafariMobile()) {
			document.querySelector('html.mobile').classList.add('prevent-scrolling'); // ios safari fix
		}
	}
};

const removePreventScrolling = () => {
	if (document.querySelector('.prevent-scrolling') !== null) {
		document.getElementById('mobile-content-wrapper') &&
			document.getElementById('mobile-content-wrapper').classList.remove('mobile-content-wrapper--fixed');
		document.querySelector('body').classList.remove('prevent-scrolling');
		if (document.querySelector('html.mobile') !== null) {
			document.querySelector('html.mobile').classList.remove('prevent-scrolling');
		}
	}
};

const getUrlParam = function (param) {
	const queryString = window.location.search;
	const urlParams = new URLSearchParams(queryString);
	return urlParams.get(param);
};

const shouldShowSexoleModal = function () {
	return (
		!window.Flux.Guest.isLoggedIn() &&
		!window.Flux.Guest.isAnonymousVoicecall() &&
		window.Flux.Browser.isSexole() &&
		!window.Flux.Browser.getLocalItem('sexoleLogin') &&
		(window.location.hostname === 'sexole.com' || window.location.hostname === 'www.sexole.com' || window.location.hostname === 'm.sexole.com')
	);
};

const shouldShowEsAVSModal = function () {
	return !getCookie(Constants.CookieNames.FORCED_AVS) && VXConfig.showEsAVSModal;
};

/**
 * Checks if an API-relevant cookie exists
 * @returns {boolean}
 */
const existsApiCookie = () => Boolean(getCookie(Constants.CookieNames.FORCED_AVS));

/**
 * A function that takes an array and removes any duplicate objects based on their id property.
 *
 * @param {Array} array - The array to remove duplicates from.
 * @return {Array} The array with duplicate objects removed.
 */
function arrayUniqueById(array) {
	const a = array.concat();
	for (let i = 0; i < a.length; ++i) {
		for (let j = i + 1; j < a.length; ++j) {
			if (a[i].id === a[j].id) {
				a.splice(j--, 1);
			}
		}
	}

	return a;
}

function isStartPage() {
	return window.location.pathname === '/' || window.location.pathname === '/' + VXConfig.language + '/';
}

function scrollToParam() {
	const queryString = window.location.search;
	const urlParams = new URLSearchParams(queryString);
	const scrollTo = urlParams.get('scrollTo');

	if (scrollTo) {
		setTimeout(() => {
			const elem = document.getElementById(scrollTo);
			window.scrollTo({ top: elem.offsetTop });
		}, 0);
	}
}

function getCurrencySymbol() {
	return Constants.CurrencySymbol.VXC;
}

function getCurrencyIcon() {
	return '-icon-vxcoin';
}

function getCurrencyDecimals(currency = null) {
	currency = currency || Constants.Currency.VXC;
	return shouldStripZeros(currency) ? 0 : 2;
}

function convertDateToUTC(date) {
	return new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(), date.getUTCHours(), date.getUTCMinutes(), date.getUTCSeconds());
}

// sort component blocks
const sortAndFilterBlockConfigs = (blockConfigs) =>
	blockConfigs
		.slice() // Create a shallow copy to avoid mutating the original array
		.sort((a, b) => {
			if (a.show === b.show) {
				return 0; // Maintain original order for equal values
			}
			if (a.show === 0) {
				return 1; // Push items with show: 0 to the end
			}
			if (b.show === 0) {
				return -1; // Push items with show: 0 to the end
			}
			return a.show - b.show; // Sort in ascending order
		})
		.filter((config) => config.show !== 0); // Remove items with show: 0

function setSeo(title, description) {
	if (title) {
		document.title = title;
	}

	const metaDescription = document.querySelector('meta[name="description"]');
	if (description && metaDescription) {
		metaDescription.setAttribute('content', description || '');
	}
}

export {
	addClass,
	currentYPosition,
	removeClass,
	scrollToElem,
	scrollContainerToElem,
	disableTransition,
	doFetch,
	getUrlWithinsertedGetParam,
	getUrlParams,
	getUrlParam,
	updateURLParameter,
	removeUrlParam,
	setUrlParameters,
	formatChatPrice,
	formatCurrency,
	formatDuration,
	formatLargeNumber,
	formatMessagePriceWithFreeMessages,
	formatNumberToLength,
	replaceTranslationMarkers,
	isMobileRequest,
	isTouchDevice,
	formatDateHM,
	getCookie,
	setCookie,
	existsApiCookie,
	gaHelper,
	gtagHelper,
	urlWithQuery,
	mergeDeep,
	parseQuery,
	generateHash,
	throttle,
	supportsPassiveEvents,
	getSymbolForCurrency,
	capitalizeFirstLetter,
	getCompleteDataset,
	isLocalItemEnabled,
	isCookieCategoryEnabled,
	openCookieConsent,
	addPreventScrolling,
	removePreventScrolling,
	getElementHeight,
	getElementWidth,
	getOffsetTop,
	getOffsetLeft,
	shouldShowSexoleModal,
	arrayUniqueById,
	isStartPage,
	scrollToParam,
	shouldShowEsAVSModal,
	getCurrencySymbol,
	getCurrencyIcon,
	getStaticAmount,
	convertDateToUTC,
	eurToCoin,
	coinToEur,
	sortAndFilterBlockConfigs,
	absoluteToRelativeURL,
	setSeo,
};
